"use client";
import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { NavItem } from "@/interfaces/menuItems";
import { DEFAULT_PLACEHOLDER_URL } from "@/constants/common";
import { useStaffHook } from "@/hooks/useStaffHook";
import { ProzUser } from "@/interfaces/account";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/shadcn/avatar";
import { UserSearch, X } from "lucide-react";
import { useAppDispatch } from "@/lib/store/hooks";
import { useProfileHook } from "@/hooks/useProfileHook";
import { setNavigationSliceBits } from "@/lib/store/features/navigation/navigationSlice";
import Link from "next/link";
import { signOut } from "next-auth/react";
import { Button } from "@/components/shadcn/button";
import { deleteCookie, getInitials, hasTag } from "@/utils/helpers";
import NavItemEditIcon from "@/components/navigation/navItemEditIcon/NavItemEditIcon";
import { logOutUser } from "@/lib/store/features/profile/profileSlice";

interface ProfileNavProps {
  profileNavs: NavItem[];
  user: ProzUser;
}

interface ProfileNavProps {
  profileNavs: NavItem[];
  user: ProzUser;
  mainNavs: NavItem[];
  entityId: number;
  isAdmin: boolean;
  showNextphase: boolean;
  isAllowedLinkEditors: boolean;
  hideAllDropDowns: () => void;
  setEditableNavItem: (navItem: NavItem) => void;
  toggleEditForm: (update_nav_item?: boolean) => void;
}

const ProfileNav: React.FC<ProfileNavProps> = ({
  profileNavs,
  user,
  mainNavs,
  entityId,
  isAdmin,
  showNextphase,
  isAllowedLinkEditors,
  hideAllDropDowns,
  setEditableNavItem,
  toggleEditForm,
}) => {
  const [first, setfirst] = useState(false);
  const { getStaffById } = useStaffHook();
  const { stopPseudoSession } = useProfileHook();
  const dispatch = useAppDispatch();

  const getImage = (): string => {
    if (user) {
      let imgUrl: string | undefined = "";
      // if (user.is_admin) {
      //   imgUrl = getStaffById(user?.entity_id)?.url ? getStaffById(user?.entity_id)?.url : "";
      // }
      imgUrl = sessionStorage.getItem("userImageUrl") || "";
      return imgUrl ? imgUrl : (user?.resource_image_url as string);
    }
    return DEFAULT_PLACEHOLDER_URL;
  };

  const togglePseudoPopup = (e: any) => {
    dispatch(setNavigationSliceBits({ bitToSet: "showPseudoPopup", value: true }));
  };

  const logout = () => {
    signOut({ redirect: true, callbackUrl: "/next" });
    process.env.NEXT_PUBLIC_NODE_ENV === "development"
      ? deleteCookie("devPHPSESSID")
      : deleteCookie("PHPSESSID");
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logOutUser());
  };

  return (
    <DropdownMenu open={first} onOpenChange={() => setfirst(!first)}>
      <DropdownMenuTrigger>
        <Avatar
          className="cursor-pointer"
          onClick={() => {
            setfirst(!first);
          }}
        >
          <AvatarImage src={getImage()} />
          <AvatarFallback>
            {getInitials(
              (user?.site_name
                ? user?.site_name
                : user?.name
                  ? user?.name
                  : user?.username) as string
            )}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mt-4 min-w-[250px] mr-5 p-1 dark:bg-black">
        {/* These are the My ProZ and Admin buttons */}
        <div className="flex flex-col py-2 px-2 gap-2">
          {mainNavs.map((menuItem) => {
            const showDirectLink =
              hasTag("directlink", menuItem.tags) &&
              (menuItem.access_type !== "liv_free" || entityId > 0) &&
              (!hasTag("admin", menuItem.tags) || isAdmin) &&
              (!hasTag("nextphase", menuItem.tags) || showNextphase);

            return (
              showDirectLink && (
                <div
                  key={menuItem.nav_item_id}
                  className=""
                  onClick={() => {
                    setfirst(!first);
                  }}
                >
                  <Link
                    onMouseEnter={(e) => hideAllDropDowns()}
                    href={menuItem.link && menuItem.link.replace("ng/", "")}
                    target={menuItem.target}
                    className={`
                                group 
                                bg-secondary
                                flex items-center
                                relative w-full
                                gap-2 px-4 py-3
                                rounded-xl
                                transition-all duration-200
                                text-center
                                text-black dark:text-accent-foreground
                                hover:bg-secondary
                                hover:dark:bg-dark
                                hover:text-primary
                                cursor-pointer 
                              `}
                    aria-label={menuItem.name}
                  >
                    <p className="text-grey-800 dark:text-white group-hover:text-primary text-sm text-center transition-all duration-200">
                      {menuItem.name}
                      {hasTag("new", menuItem.tags) && (
                        <span className="bg-green-gradient rounded-md text-sm px-2 text-slate-50 ml-1">
                          new
                        </span>
                      )}
                    </p>
                    {isAllowedLinkEditors && (
                      <span className="hidden group-hover:block group-hover:text-primary">
                        <NavItemEditIcon
                          onEditIconClick={() => {
                            setEditableNavItem(menuItem);
                            toggleEditForm();
                          }}
                        />
                      </span>
                    )}
                  </Link>
                </div>
              )
            );
          })}
        </div>

        {(user.is_admin || user.is_pseudo) && (
          <DropdownMenuItem className="flex items-center justify-center p-1">
            <span
              onClick={(e) => togglePseudoPopup(e)}
              className="w-full flex items-center justify-start gap-2 hover:bg-primary hover:!text-white dark:hover:bg-dark dark:text-slate-100 rounded-md p-3 pseudo_menu"
              role="button"
            >
              <UserSearch />
              <span className="flex-grow">
                {user.is_pseudo ? `Viewing as ${user?.site_name}` : "Impersonate User"}
              </span>
              {user.is_pseudo && (
                <X
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    stopPseudoSession();
                  }}
                />
              )}
            </span>
          </DropdownMenuItem>
        )}
        {profileNavs.map((profileNav, index) => (
          <DropdownMenuItem
            key={index}
            className="p-1"
            onClick={() => {
              setfirst(!first);
            }}
          >
            {!profileNav.tailwind.includes("logout") ? (
              <Link
                prefetch={true}
                href={"/profile/" + user.entity_id}
                target={profileNav.target}
                className='w-full flex items-center justify-start gap-2 hover:bg-primary hover:!text-white dark:hover:bg-dark dark:text-slate-100 rounded-md p-3 pseudo_menu"'
              >
                {profileNav.name}
              </Link>
            ) : (
              <Button variant="destructive" className="w-full" onClick={() => logout()}>
                {profileNav.name}
              </Button>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileNav;
